/* Email → Provider tab: choose the sending service, fill its fields, send a test, save. */
import { useMemo, useState } from "react";
import { useResetAdminEmail, useTestAdminEmail, useUpdateAdminEmail } from "../../../data/hooks";
import { ApiError } from "../../../data/provider";
import type { EmailConfig, EmailConfigInput, EmailProviderId, EmailTestResult, Lang } from "../../../data/types";
import { useI18n } from "../../../i18n";
import { useSession } from "../../../app/session";
import { Alert, Button, Card, Confirm, Grid, SelectField, TextField, Toggle, errMsg, useToast } from "../../../ui";
import { PROVIDERS, emailDraft, providerById, toInput, withProvider, type EmailDraft, type ProviderDraft } from "./catalog";
import ProviderFields from "./ProviderFields";
import ProviderGuide from "./ProviderGuide";

export function ProviderCards({ value, onPick, exclude, testPrefix }: { value: EmailProviderId; onPick: (id: EmailProviderId) => void; exclude?: EmailProviderId[]; testPrefix: string }) {
  const { L } = useI18n();
  return (
    <div className="prov-grid" role="radiogroup" aria-label={L("Email provider", "مزوّد البريد")}>
      {PROVIDERS.filter((p) => !(exclude ?? []).includes(p.id)).map((p) => (
        <button key={p.id} type="button" role="radio" aria-checked={value === p.id} className={"prov-card" + (value === p.id ? " on" : "")} onClick={() => onPick(p.id)} data-testid={testPrefix + "-" + p.id}>
          <span className={"ini " + p.tone} aria-hidden="true">{p.initials}</span>
          <span><b>{L(p.name, p.nameAr)}</b><span className="xs muted" style={{ display: "block" }}>{p.kind === "api" ? "API" : p.kind === "smtp" ? "SMTP" : L("Developer", "للمطورين")}</span></span>
        </button>
      ))}
    </div>
  );
}

/** The candidate configuration the form currently describes. */
export function candidate(d: EmailDraft): EmailConfigInput {
  const out: EmailConfigInput = { provider: toInput(d.provider), fallback: d.useFallback && d.provider.provider !== "log" ? toInput(d.fallback) : null, fromName: d.fromName.trim(), fromAddress: d.fromAddress.trim(),
    replyTo: d.replyTo.trim(), sandboxTo: d.sandboxTo.trim() };
  out.dailyLimit = d.dailyLimit.trim() === "" ? 0 : Number(d.dailyLimit);
  return out;
}

function TestResult({ r }: { r: EmailTestResult }) {
  const { L } = useI18n();
  return r.ok
    ? <Alert tone="ok" testId="email-test-result" title={L("Test email sent to " + r.to, "تم إرسال رسالة تجريبية إلى " + r.to)}
        desc={<span className="xs">{L("Accepted by the provider in ", "قبلها المزوّد خلال ")}<span className="num">{r.ms}</span> ms{r.providerMessageId ? <> · <code dir="ltr">{r.providerMessageId}</code></> : null}</span>} />
    : <Alert tone="bad" testId="email-test-result" title={L("The test email was not sent", "لم تُرسل الرسالة التجريبية")}
        desc={<span className="stack" style={{ gap: 4 }}><code className="xs" dir="ltr" style={{ wordBreak: "break-all" }}>{r.error}</code>{r.hint && <span className="sm" data-testid="email-test-hint">{r.hint}</span>}</span>} />;
}

export default function ProviderTab({ cur, onDns }: { cur: EmailConfig; onDns?: () => void }) {
  const { L, lang } = useI18n();
  const toast = useToast();
  const { user } = useSession();
  const save = useUpdateAdminEmail();
  const test = useTestAdminEmail();
  const reset = useResetAdminEmail();
  const [d, setD] = useState<EmailDraft>(() => emailDraft(cur));
  const [fe, setFe] = useState<Record<string, string>>({});
  const [to, setTo] = useState(user?.email ?? "");
  const [testLang, setTestLang] = useState<Lang>(lang);
  const [result, setResult] = useState<EmailTestResult | null>(null);
  const [confirm, setConfirm] = useState(false);
  const info = providerById(d.provider.provider);

  const patch = (p: Partial<EmailDraft>, clear: string[] = []) => {
    setD({ ...d, ...p }); setResult(null);
    if (clear.length) { const n = { ...fe }; clear.forEach((k) => delete n[k]); setFe(n); }
  };
  const onProv = (which: "provider" | "fallback") => (pd: ProviderDraft) => {
    const n = { ...fe };
    for (const k of Object.keys(n)) if (k.startsWith(which + ".") || k.startsWith("config." + which + ".")) delete n[k];
    setFe(n); setD({ ...d, [which]: pd }); setResult(null);
  };
  const cand = useMemo(() => candidate(d), [d]);
  const apply = (e: unknown) => {
    if (e instanceof ApiError && e.fields) {
      const f: Record<string, string> = {};
      for (const [k, v] of Object.entries(e.fields)) f[k.replace(/^config\./, "")] = v;
      setFe(f);
      toast(L("Please fix the highlighted fields", "يرجى تصحيح الحقول المحددة"), "bad");
      return;
    }
    toast(errMsg(e), "bad");
  };
  const runTest = async () => {
    setFe({}); setResult(null);
    try { setResult(await test.mutateAsync({ to: to.trim(), lang: testLang, config: cand })); } catch (e) { apply(e); }
  };
  const doSave = async () => {
    setFe({});
    try {
      const v = await save.mutateAsync(cand);
      setD(emailDraft(v));
      toast(L("Email settings saved — new emails use " + (providerById(v.provider.provider)?.name ?? v.provider.provider), "تم حفظ إعدادات البريد — الرسائل الجديدة تُرسل عبر " + (providerById(v.provider.provider)?.nameAr ?? v.provider.provider)));
    } catch (e) { apply(e); }
  };

  return (
    <Grid cols="2-1">
      <div className="stack">
        <Card title={L("Sending service", "خدمة الإرسال")} sub={L("Where StartHR sends account, billing and HR emails from", "الخدمة التي يرسل منها StartHR رسائل الحسابات والفوترة والموارد البشرية")} testId="email-provider-card">
          <div className="stack">
            <ProviderCards value={d.provider.provider} onPick={(id) => onProv("provider")(withProvider(d.provider, id))} testPrefix="prov" />
            {fe["provider.provider"] && <Alert tone="bad" title={fe["provider.provider"]} />}
            {info && <h3 className="sm" style={{ margin: "4px 0 0" }} data-testid="prov-selected">{L(info.name, info.nameAr)}</h3>}
            <ProviderFields d={d.provider} onChange={onProv("provider")} saved={cur.provider} fe={fe} prefix="provider." testPrefix="em" />
          </div>
        </Card>
        <Card title={L("Sender", "المرسل")} testId="email-sender-card">
          <div className="form2">
            <TextField label={L("From name", "اسم المرسل")} req value={d.fromName} onChange={(v) => patch({ fromName: v }, ["fromName"])} err={fe.fromName} testId="em-from-name" />
            <TextField label={L("From address", "عنوان المرسل")} req value={d.fromAddress} onChange={(v) => patch({ fromAddress: v }, ["fromAddress"])} err={fe.fromAddress} dir="ltr" placeholder="noreply@example.sa" testId="em-from"
              hint={L("Must be verified with the provider (domain or single address).", "يجب توثيقه لدى المزوّد (النطاق أو العنوان).")} />
            <TextField label={L("Reply-to (optional)", "الرد إلى (اختياري)")} value={d.replyTo} onChange={(v) => patch({ replyTo: v }, ["replyTo"])} err={fe.replyTo} dir="ltr" placeholder="support@example.sa" testId="em-reply" />
            <TextField label={L("Daily limit (0 = none)", "الحد اليومي (0 = بلا حد)")} value={d.dailyLimit} onChange={(v) => patch({ dailyLimit: v }, ["dailyLimit"])} err={fe.dailyLimit} mode="numeric" dir="ltr" testId="em-limit"
              hint={L("Sent today: " + cur.sentToday, "أُرسل اليوم: " + cur.sentToday)} />
          </div>
        </Card>
        <Card title={L("Sandbox / staging", "وضع التجربة")} testId="email-sandbox-card">
          <TextField label={L("Redirect every email to (optional)", "تحويل كل الرسائل إلى (اختياري)")} value={d.sandboxTo} onChange={(v) => patch({ sandboxTo: v }, ["sandboxTo"])} err={fe.sandboxTo} dir="ltr" placeholder="qa@example.sa" testId="em-sandbox"
            hint={L("While set, customers receive nothing — all mail goes to this one inbox with the original recipient in the subject.", "عند ضبطه لا يستلم العملاء شيئًا — تذهب كل الرسائل إلى هذا البريد مع ذكر المستلم الأصلي في العنوان.")} />
          {d.sandboxTo.trim() && <div className="mt"><Alert tone="warn" title={L("Sandbox is on", "وضع التجربة مفعّل")} testId="em-sandbox-on" /></div>}
        </Card>
        {d.provider.provider !== "log" && (
          <Card title={L("Fallback provider", "مزوّد احتياطي")} testId="email-fallback-card"
            actions={<Toggle checked={d.useFallback} onChange={(v) => patch({ useFallback: v }, Object.keys(fe).filter((k) => k.startsWith("fallback")))} label={L("Use a fallback provider", "استخدام مزوّد احتياطي")} testId="em-use-fallback" />}>
            {d.useFallback ? (
              <div className="stack">
                <p className="sm muted" style={{ margin: 0 }}>{L("If the main provider is down or rejects the login, the message is retried here immediately.", "إذا تعطل المزوّد الرئيسي أو رفض الدخول تُعاد المحاولة هنا فورًا.")}</p>
                <SelectField label={L("Fallback service", "الخدمة الاحتياطية")} value={d.fallback.provider} onChange={(v) => onProv("fallback")(withProvider(d.fallback, v as EmailProviderId))} err={fe["fallback.provider"]} testId="fb-provider"
                  options={PROVIDERS.filter((p) => p.id !== "log" && p.id !== d.provider.provider).map((p) => [p.id, L(p.name, p.nameAr)] as [string, string])} />
                <ProviderFields d={d.fallback} onChange={onProv("fallback")} saved={cur.fallback} fe={fe} prefix="fallback." testPrefix="fb" />
              </div>
            ) : <p className="sm muted" style={{ margin: 0 }}>{L("Off — failed sends are retried with the main provider after 1 min, 5 min, 30 min and 2 h.", "متوقف — تُعاد المحاولة مع المزوّد الرئيسي بعد دقيقة و5 دقائق و30 دقيقة وساعتين.")}</p>}
          </Card>
        )}
        <div className="row">
          <Button variant="pri" icon="check" loading={save.isPending} onClick={doSave} data-testid="em-save">{L("Save settings", "حفظ الإعدادات")}</Button>
          {cur.source === "database" && <Button variant="ghost" onClick={() => setConfirm(true)} data-testid="em-reset">{L("Use server defaults", "استخدام إعدادات الخادم")}</Button>}
        </div>
      </div>
      <div className="stack">
        <ProviderGuide provider={d.provider.provider} open={cur.provider.provider === "log" || d.provider.provider !== cur.provider.provider} onDns={onDns} />
        <Card title={L("Send a test email", "إرسال رسالة تجريبية")} sub={L("Uses the settings on this page — even before you save them.", "يستخدم الإعدادات الظاهرة في الصفحة — حتى قبل حفظها.")} testId="email-test-card">
          <div className="stack">
            <TextField label={L("Send to", "إرسال إلى")} req value={to} onChange={(v) => { setTo(v); setFe({ ...fe, to: "" }); }} err={fe.to} dir="ltr" testId="em-test-to" />
            <SelectField label={L("Language", "اللغة")} value={testLang} onChange={(v) => setTestLang(v as Lang)} testId="em-test-lang" options={[["en", "English"], ["ar", "العربية"]]} />
            <Button icon="mail" loading={test.isPending} onClick={runTest} data-testid="em-test">{L("Send test", "إرسال تجربة")}</Button>
            {result && <TestResult r={result} />}
          </div>
        </Card>
      </div>
      {confirm && <Confirm title={L("Use the server defaults?", "استخدام إعدادات الخادم؟")} confirmLabel={L("Reset", "إعادة التعيين")} onClose={() => setConfirm(false)} busy={reset.isPending}
        onConfirm={async () => { setConfirm(false); try { const v = await reset.mutateAsync(undefined); setD(emailDraft(v)); setFe({}); toast(L("Back to the server's email settings", "تمت العودة لإعدادات البريد في الخادم")); } catch (e) { toast(errMsg(e), "bad"); } }}
        body={L("The settings saved here are removed and the EMAIL_* / SMTP_* values of the server environment apply again.", "تُحذف الإعدادات المحفوظة هنا وتعود قيم EMAIL_* وSMTP_* من بيئة الخادم.")} />}
    </Grid>
  );
}
